/**
 * The SUPPLIES program — things a school orders in bulk that are SHIPPED, not
 * served: gloves and plates (SUPPLIES), advertising material (SWAG), and
 * shelf-stable backup meals (SHELF_STABLE_MEALS).
 *
 * Deliberately NOT part of `menu-taxonomy.ts`:
 *
 *   - `MENU_PROGRAM_CODES_BY_SERVICE_SEASON` forces every program into a
 *     service season; supplies belong to neither.
 *   - `MENU_LINE_CODES` seeds a `MenuLine` row for every school, feeds the
 *     customers setup-wizard enums, and is offered as an authorable
 *     MenuTemplate line with a required mealtime — "SWAG lunch" is nonsense.
 *   - Nothing here is a Menu / MenuItem / CartItem: supply items live in their
 *     own catalog (`SupplyItem`) and orders in `SupplyOrder`, with an
 *     estimated SHIP WINDOW instead of a service date.
 *
 * The audience machinery fails closed to INTERNAL for unknown line codes, so
 * keeping these out of the menu taxonomy leaks nothing either way. Reads as a
 * "program" to ops through `SUPPLY_PROGRAM_LABEL` + the per-line labels and
 * badge styles, exactly like the menu programs render.
 *
 * Mirrored by the Prisma `SupplyLineCode` enum; a schema test pins the two
 * together. Adding a line here without a label / style / default ship window
 * fails to compile.
 */

import type { MenuBadgeColorKey } from './menu-line-style'

export const SUPPLY_PROGRAM_CODE = 'SUPPLIES' as const

export const SUPPLY_PROGRAM_LABEL = 'Supplies'

export const SUPPLY_LINE_CODES = ['SUPPLIES', 'SWAG', 'SHELF_STABLE_MEALS'] as const

export type SupplyLineCode = (typeof SUPPLY_LINE_CODES)[number]

export const SUPPLY_LINE_LABELS: Readonly<Record<SupplyLineCode, string>> = Object.freeze({
  SUPPLIES: 'Supplies',
  SWAG: 'Swag',
  SHELF_STABLE_MEALS: 'Shelf-Stable Meals',
})

/** Short blurb under each line's heading in the school-facing catalog. */
export const SUPPLY_LINE_DESCRIPTIONS: Readonly<Record<SupplyLineCode, string>> = Object.freeze({
  SUPPLIES: 'Gloves, plates, cutlery and other service supplies',
  SWAG: 'Posters, flyers and other Ordo promotional material',
  SHELF_STABLE_MEALS: 'Shelf-stable backup meals for days the kitchen cannot deliver',
})

/** Badge palette per line, reusing the menu-line badge keys so chips match. */
export const SUPPLY_LINE_STYLES: Readonly<Record<SupplyLineCode, { colorKey: MenuBadgeColorKey }>> = Object.freeze({
  SUPPLIES: { colorKey: 'gray' },
  SWAG: { colorKey: 'violet' },
  SHELF_STABLE_MEALS: { colorKey: 'orange' },
})

/** An estimated shipping window in calendar days, inclusive. */
export interface SupplyShipWindow {
  minDays: number
  maxDays: number
}

/**
 * Per-line default ship window, used when an item carries none of its own.
 * Gloves and shelf-stable meals from the same supplier ship differently, so
 * the item's own window (when set) always wins — see `resolveSupplyShipWindow`.
 */
export const SUPPLY_LINE_DEFAULT_SHIP_WINDOW: Readonly<Record<SupplyLineCode, SupplyShipWindow>> = Object.freeze({
  SUPPLIES: { minDays: 3, maxDays: 5 },
  SWAG: { minDays: 5, maxDays: 10 },
  SHELF_STABLE_MEALS: { minDays: 5, maxDays: 7 },
})

export function isSupplyLineCode(value: unknown): value is SupplyLineCode {
  return typeof value === 'string' && (SUPPLY_LINE_CODES as readonly string[]).includes(value)
}

/**
 * The window an item ships in: its own `shipMinDays`/`shipMaxDays` when BOTH
 * are set and sane, else its line's default. Never returns an inverted window.
 */
export function resolveSupplyShipWindow(item: {
  lineCode: SupplyLineCode
  shipMinDays: number | null | undefined
  shipMaxDays: number | null | undefined
}): SupplyShipWindow {
  const { shipMinDays, shipMaxDays } = item
  if (
    typeof shipMinDays === 'number' &&
    typeof shipMaxDays === 'number' &&
    Number.isInteger(shipMinDays) &&
    Number.isInteger(shipMaxDays) &&
    shipMinDays >= 0 &&
    shipMaxDays >= shipMinDays
  ) {
    return { minDays: shipMinDays, maxDays: shipMaxDays }
  }
  return SUPPLY_LINE_DEFAULT_SHIP_WINDOW[item.lineCode]
}

/**
 * "Ships in 3-5 days" / "Ships in 1 day" / "Ships in 1-2 weeks". Windows of
 * a fortnight or more read in weeks (rounded outward so the promise is
 * never tighter than the days). Null = no estimate available.
 */
export function supplyShipWindowLabel(window: SupplyShipWindow | null | undefined): string {
  if (!window) {
    return 'Ship time to be confirmed'
  }
  const { minDays, maxDays } = window
  if (minDays >= 14 && maxDays >= 14) {
    const minWeeks = Math.floor(minDays / 7)
    const maxWeeks = Math.ceil(maxDays / 7)
    return minWeeks === maxWeeks
      ? `Ships in ${minWeeks} week${minWeeks === 1 ? '' : 's'}`
      : `Ships in ${minWeeks}-${maxWeeks} weeks`
  }
  if (minDays === maxDays) {
    return minDays === 0 ? 'Ships same day' : `Ships in ${minDays} day${minDays === 1 ? '' : 's'}`
  }
  return `Ships in ${minDays}-${maxDays} days`
}

/** The widest window across an order's lines — what the order as a whole promises. */
export function widestSupplyShipWindow(windows: ReadonlyArray<SupplyShipWindow>): SupplyShipWindow | null {
  if (windows.length === 0) {
    return null
  }
  return windows.reduce<SupplyShipWindow>(
    (acc, w) => ({ minDays: Math.min(acc.minDays, w.minDays), maxDays: Math.max(acc.maxDays, w.maxDays) }),
    { minDays: Number.POSITIVE_INFINITY, maxDays: 0 },
  )
}
